import { range } from "lodash-es";

import { isCoordinate, rectangle } from "@/math/coordinates";
import type { GroupIteration } from "@/model/board";
import type { Rectangle, RelativeCoordinate } from "@/model/coordinates";
import { relativeCoord } from "@/model/coordinates";
import type { Iteration } from "@/model/session";
import { fixLine } from "@/model/shape";
import { useSessionStore } from "@/store/session";
import { useGlobalI18n } from "@/translations/i18n";

import { BoardLocation, borderLines } from "./BoardLocation";

const backlogColumn = -2;
const groupColumn = -1;
const iterationRow = -1;

type PlanningBoardType = "team" | "art";

const i18nMap = {
  team: { row: /*$t*/ "general.namedTeam", backlog: /*$t*/ "backlogBoard.programBacklog" },
  art: { row: /*$t*/ "general.namedArt", backlog: /*$t*/ "backlogBoard.solutionBacklog" },
};

export interface GroupIterations {
  id: string;
  name: string;
  current?: boolean;
  iterations: GroupIteration[];
  externalOrgIds?: string[];
}

const { t } = useGlobalI18n();

export class PlanningBoardLocation extends BoardLocation {
  private readonly iterations: Iteration[];
  private readonly iteration: Iteration;
  private readonly group?: GroupIterations;
  readonly groupIteration?: GroupIteration;
  readonly iterationId: number | null;
  readonly groupId: string | null;

  constructor(
    private type: PlanningBoardType,
    private groups: GroupIterations[],
    public left: number,
    public top: number,
  ) {
    super();
    this.iterations = groups[0].iterations;
    // During initial load, groups[0].iterations will be undefined if the first swimlane is a team
    // If iterations is an empty array, we also fall back to session iterations so that the right
    // board shapes are calculated
    if (!this.iterations?.length) {
      this.iterations = useSessionStore().iterations;
    }
    this.iteration = this.iterations[left];
    this.iterationId = this.iteration?.id ?? null;
    this.group = groups[top];
    this.groupIteration = this.group?.iterations ? this.group.iterations[left] : undefined;
    this.groupId = this.group?.id || null;
  }

  static of(
    type: PlanningBoardType,
    groups: GroupIterations[],
    coordOrLeft: RelativeCoordinate | number | string,
    top?: number,
  ): PlanningBoardLocation {
    if (typeof coordOrLeft === "string") {
      const [left, top] = coordOrLeft.split(",");
      return new PlanningBoardLocation(type, groups, +left, +top);
    }
    return isCoordinate(coordOrLeft)
      ? PlanningBoardLocation.fromCoordinate(type, groups, coordOrLeft)
      : new PlanningBoardLocation(type, groups, coordOrLeft, top!);
  }

  static ofTeams(
    teams: GroupIterations[],
    coordOrLeft: RelativeCoordinate | number | string,
    top?: number,
  ): PlanningBoardLocation {
    return PlanningBoardLocation.of("team", teams, coordOrLeft, top);
  }

  static ofArts(
    arts: GroupIterations[],
    coordOrLeft: RelativeCoordinate | number | string,
    top?: number,
  ): PlanningBoardLocation {
    return PlanningBoardLocation.of("art", arts, coordOrLeft, top);
  }

  static fromCoordinate(
    type: PlanningBoardType,
    groups: GroupIterations[],
    coord: RelativeCoordinate,
  ): PlanningBoardLocation {
    // During initial load, groups[0].iterations will be undefined if the first swimlane is a team
    // while we wait for the API response
    let iterations = groups[0].iterations;
    if (!iterations?.length) {
      iterations = useSessionStore().iterations;
    }
    const rawLeft = coord.x * (iterations.length + 1) - 1;
    const left = rawLeft < -0.5 ? backlogColumn : Math.floor(rawLeft);
    const top = Math.floor(coord.y * (groups.length + 0.5) - 0.5);
    return new PlanningBoardLocation(type, groups, left, top);
  }

  override matches(coord: RelativeCoordinate) {
    const loc = PlanningBoardLocation.fromCoordinate(this.type, this.groups, coord);
    return (
      ((this.left === backlogColumn || this.top === iterationRow) && this.left === loc.left) ||
      ((this.left === groupColumn || this.left === loc.left) && this.top === loc.top)
    );
  }

  iterationName() {
    switch (this.left) {
      case backlogColumn:
        return t(i18nMap[this.type].backlog);
      case groupColumn:
        return "";
      default:
        return t("general.namedIteration", { name: this.iteration.name });
    }
  }

  groupName() {
    if (this.left === backlogColumn || !this.group) {
      return "";
    }
    if (!this.group.id) {
      return this.group.name;
    }
    return t(i18nMap[this.type].row, { name: this.group.name });
  }

  names() {
    return [this.groupName(), this.iterationName()].filter((name) => !!name);
  }

  override id() {
    return this.left + "," + this.top;
  }

  override get name() {
    return this.iterationName() + " " + this.groupName();
  }

  override get bounds(): Rectangle<RelativeCoordinate> {
    const cols = this.iterations.length + 1;
    const rows = this.groups.length + 0.5;
    const [x, width] =
      this.left < 0 ? [(this.left / 2 + 1) / cols, 0.5 / cols] : [(this.left + 1) / cols, 1 / cols];
    const [y, height] = this.top < 0 ? [0, 0.5 / rows] : [(this.top + 0.5) / rows, 1 / rows];
    if (this.left === backlogColumn && this.top >= 0) {
      return rectangle({
        from: relativeCoord(x, 0.5 / rows),
        size: relativeCoord(width, 1 - 0.5 / rows),
      });
    }
    return rectangle({ from: relativeCoord(x, y), size: relativeCoord(width, height) });
  }

  override get shapes() {
    const rows = this.groups.length + 0.5;
    const cols = this.iterations.length + 1;
    return [
      ...borderLines,
      fixLine("v0", relativeCoord(0.5 / cols, 0), relativeCoord(0.5 / cols, 1)),
      ...range(1, cols).map((col) =>
        fixLine("v" + col, relativeCoord(col / cols, 0), relativeCoord(col / cols, 1)),
      ),
      fixLine("h0", relativeCoord(0, 0.5 / rows), relativeCoord(1, 0.5 / rows)),
      ...range(1.5, this.groups.length).map((row) =>
        fixLine("h" + row, relativeCoord(0, row / rows), relativeCoord(1, row / rows)),
      ),
    ];
  }
}
